/**
 * Refactor Proposals view (doc 147 §6.6 E3 + PR Q2 vote-loop / Fix in Sandbox).
 *
 * PR Q2 adds:
 *  - Per-proposal vote ▲/▼ with retraction (localStorage-backed; the
 *    aggregator cloud function is a Phase 2 follow-up).
 *  - "Fix in Sandbox" CTA that opens SandboxEditorView seeded with the
 *    proposal's entityId.
 *  - Group by kind (split-god-entity, add-bridge-tag, break-cycle).
 *  - "Draft with AI" slide-over: generates PR description drafts,
 *    allows follow-up questions in a thread.
 */
import { useMemo, useState, useCallback } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import {
  buildRefactorProposals,
  type RefactorProposal,
  type ProposalKind,
} from '../state/refactorProposals.js';
import { useFocusScroll } from '../state/useFocusScroll.js';
import { Icon } from '../components/Icon.js';
import { ErrorState } from '../components/ErrorState.js';
import { navigate } from '../state/hashRouter.js';
import {
  readVotes,
  writeVote,
  clearVote,
  type VoteDirection,
} from '../state/qualityPersistence.js';
import { useInlineError, persistErrorMessage } from '../state/useInlineError.js';
import { AiMarkdown } from '../components/AiMarkdown.js';
import { AiThinking } from '../components/AiThinking.js';
import { useAiSuggest } from '../state/useAiSuggest.js';
import { refactorProposalDraftPrompt } from '../state/aiPrompts.js';
import type { AiSuggestOptions } from '../state/aiPrompts.js';
import type { AskAtlasResult } from '../state/atlasApiTypes.js';
import { streamAskAtlas } from '../firebase/atlasApi.js';

export interface RefactorProposalsViewProps {
  manifest: AtlasManifest;
  onNavigateEntity: (entityId: string) => void;
  /** Optional entity id to scroll to + highlight (from `#refactor/focus/<id>`). */
  focus?: string;
  /** Optional callback for AI suggestions; when provided, renders "✦ Draft with AI" button. */
  onAsk?: (opts: AiSuggestOptions) => Promise<AskAtlasResult>;
}

const KIND_LABELS: Record<ProposalKind, string> = {
  'split-god-entity': 'Split god entities',
  'add-bridge-tag': 'Cross-domain bridges',
  'break-cycle': 'Cycle breakers',
};

function proposalId(p: RefactorProposal): string {
  return `${p.kind}:${p.ref}`;
}

export function RefactorProposalsView({
  manifest,
  onNavigateEntity,
  focus,
  onAsk,
}: RefactorProposalsViewProps): React.JSX.Element {
  const proposals = useMemo(() => buildRefactorProposals(manifest), [manifest]);
  const [votes, setVotes] = useState<Record<string, VoteDirection>>(() => readVotes());
  const [error, showError, clearError] = useInlineError();
  useFocusScroll(focus);

  // AI draft slide-over state
  const [draftProposalId, setDraftProposalId] = useState<string | null>(null);
  const [followUpThread, setFollowUpThread] = useState<
    Array<{ question: string; answer: string; fabricated: string[] }>
  >([]);
  const [followUpInput, setFollowUpInput] = useState('');

  const defaultOnAsk = useCallback(
    (opts: AiSuggestOptions) => streamAskAtlas(opts.prompt, opts.summary, opts.knownEntityIds),
    [],
  );
  const effectiveOnAsk = onAsk ?? defaultOnAsk;

  const draftAi = useAiSuggest(effectiveOnAsk);
  const followUpAi = useAiSuggest(effectiveOnAsk);

  const activeProposal = proposals.find((p) => proposalId(p) === draftProposalId) ?? null;

  const openDraft = useCallback(
    async (proposal: RefactorProposal) => {
      setDraftProposalId(proposalId(proposal));
      setFollowUpThread([]);
      setFollowUpInput('');
      draftAi.clear();
      followUpAi.clear();
      await draftAi.suggest(refactorProposalDraftPrompt(proposal, manifest));
    },
    [manifest, draftAi, followUpAi],
  );

  const closeDraft = useCallback(() => {
    setDraftProposalId(null);
    draftAi.clear();
    followUpAi.clear();
    setFollowUpThread([]);
    setFollowUpInput('');
  }, [draftAi, followUpAi]);

  const submitFollowUp = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!followUpInput.trim() || !activeProposal || followUpAi.busy) return;
      const question = followUpInput.trim();
      setFollowUpInput('');
      const result = await followUpAi.suggest({
        prompt:
          `Context: We are drafting a refactor for "${activeProposal.title}" ` +
          `(${activeProposal.kind}). Rationale: ${activeProposal.rationale}\n\n` +
          `User question: ${question}\n\nAnswer concisely.`,
        summary: {
          entityCount: manifest.entities.length,
          domainCount: manifest.domains.length,
          domainNames: manifest.domains.map((d) => d.name),
        },
        knownEntityIds: manifest.entities.map((e) => e.id),
      });
      if (result) {
        setFollowUpThread((prev) => [
          ...prev,
          { question, answer: result.answer, fabricated: result.fabricated },
        ]);
        followUpAi.clear();
      }
    },
    [followUpInput, activeProposal, manifest, followUpAi],
  );

  const grouped = useMemo(() => {
    const out = new Map<ProposalKind, RefactorProposal[]>();
    for (const p of proposals) {
      const list = out.get(p.kind) ?? [];
      list.push(p);
      out.set(p.kind, list);
    }
    return out;
  }, [proposals]);

  const vote = (p: RefactorProposal, dir: VoteDirection): void => {
    const id = proposalId(p);
    if (votes[id] === dir) {
      const r = clearVote(id);
      if (!r.ok) { showError(persistErrorMessage(r, 'retract vote')); return; }
      const next = { ...votes };
      delete next[id];
      setVotes(next);
    } else {
      const r = writeVote(id, dir);
      if (!r.ok) { showError(persistErrorMessage(r, 'save vote')); return; }
      setVotes({ ...votes, [id]: dir });
    }
  };

  return (
    <div className="refactor-proposals">
      {error && <ErrorState message={error} onDismiss={clearError} />}
      <h2>Refactor Proposals</h2>
      <p className="subtitle">
        Heuristic refactor candidates derived from the lint engine. Vote
        to teach the engine which proposals are worth surfacing first.
        "Fix in Sandbox" opens an editable sandbox seeded with the
        target entity so you can prototype the change risk-free.
      </p>
      {proposals.length === 0 ? (
        <p className="refactor-empty">
          No refactor candidates surfaced. The model is in good shape — or
          our heuristics need more rules.
        </p>
      ) : (
        <div className="refactor-groups">
          {(Object.keys(KIND_LABELS) as ProposalKind[]).map((kind) => {
            const items = grouped.get(kind) ?? [];
            if (items.length === 0) return null;
            return (
              <section key={kind} className={`refactor-group kind-${kind}`} aria-label={KIND_LABELS[kind]}>
                <header className="refactor-group-head">
                  <h3>{KIND_LABELS[kind]}</h3>
                  <span className="refactor-group-count mono">{items.length}</span>
                </header>
                {items.map((p) => {
                  const id = proposalId(p);
                  const userVote = votes[id];
                  // Tone the confidence bar like the spec: high=brand, med=warn, low=info.
                  const confColor =
                    p.confidence >= 0.7
                      ? 'var(--brand)'
                      : p.confidence >= 0.5
                      ? 'var(--warn)'
                      : 'var(--info)';
                  return (
                    <article
                      key={id}
                      className="refactor-card"
                      data-entity-id={p.entityId}
                      data-rid={id}
                    >
                      <div className="refactor-card-body">
                        <h4 className="refactor-card-title">{p.title}</h4>
                        <p className="refactor-rationale">{p.rationale}</p>
                        <div className="refactor-confbar">
                          <span className="refactor-confbar-label">CONFIDENCE</span>
                          <div className="refactor-confbar-track">
                            <div
                              className="refactor-confbar-fill"
                              style={{ width: `${p.confidence * 100}%`, background: confColor }}
                            />
                          </div>
                          <span className="mono">{(p.confidence * 100).toFixed(0)}%</span>
                        </div>
                        <div className="refactor-meta">
                          <span className="chip">kind · <code>{p.kind}</code></span>
                          <span className="chip mono">{p.entityId}</span>
                        </div>
                      </div>
                      <div className="refactor-card-actions">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => navigate({ kind: 'sandbox', seedEntity: p.entityId })}
                        >
                          <Icon name="science" size="sm" />
                          Fix in Sandbox
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => onNavigateEntity(p.entityId)}
                        >
                          Open {p.entityId}
                        </button>
                        {onAsk !== undefined && (
                          <button
                            type="button"
                            className="btn btn-xs btn-primary"
                            onClick={() => void openDraft(p)}
                            disabled={draftAi.busy && draftProposalId === proposalId(p)}
                          >
                            ✦ Draft with AI
                          </button>
                        )}
                        <div className="refactor-vote" role="group" aria-label="Vote on this proposal">
                          <button
                            type="button"
                            className={`refactor-vote-btn up${userVote === 'up' ? ' is-active' : ''}`}
                            aria-pressed={userVote === 'up'}
                            aria-label="Vote up"
                            onClick={() => vote(p, 'up')}
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            className={`refactor-vote-btn down${userVote === 'down' ? ' is-active' : ''}`}
                            aria-pressed={userVote === 'down'}
                            aria-label="Vote down"
                            onClick={() => vote(p, 'down')}
                          >
                            ▼
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </section>
            );
          })}
        </div>
      )}

      {draftProposalId !== null && activeProposal && (
        <div className="ai-draft-panel" aria-label="AI Draft panel">
          {/* Header */}
          <div className="ai-draft-header">
            <span className="ai-draft-title">
              <span className="brand-star">✦</span>
              <h2>AI Draft</h2>
              <span className="ai-draft-chip">{activeProposal.title}</span>
            </span>
            <button
              className="btn btn-ghost btn-xs"
              aria-label="✕"
              onClick={closeDraft}
            >
              ✕
            </button>
          </div>

          {/* Draft content */}
          <div className="ai-draft-body">
            {draftAi.busy && <AiThinking label="Drafting the PR description…" />}

            {draftAi.answer && (
              <div className="ai-draft-output">
                <div className="ai-draft-output-label">PR DESCRIPTION DRAFT</div>
                <AiMarkdown text={draftAi.answer} />

                {draftAi.fabricated.length > 0 && (
                  <div className="ai-fabricated-warning">
                    ⚠ {draftAi.fabricated.join(', ')} — proposed name(s), not in schema yet
                  </div>
                )}

                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => void navigator.clipboard.writeText(draftAi.answer!)}
                >
                  📋 Copy draft
                </button>
              </div>
            )}

            {draftAi.error && (
              <ErrorState
                variant="inline"
                message={draftAi.error}
                onDismiss={draftAi.clear}
                onRetry={() => activeProposal && void draftAi.suggest(refactorProposalDraftPrompt(activeProposal, manifest))}
              />
            )}

            {/* Follow-up thread */}
            {draftAi.answer && (
              <div className="ai-followup-section">
                <div className="ai-followup-label">FOLLOW-UP</div>

                {followUpThread.map((exchange, i) => (
                  <div key={i} className="ai-followup-exchange">
                    <p className="ai-followup-question">{exchange.question}</p>
                    <AiMarkdown text={exchange.answer} />
                  </div>
                ))}

                {followUpAi.busy && <AiThinking label="Thinking…" size="small" />}

                <form onSubmit={(e) => void submitFollowUp(e)} className="ai-followup-form">
                  <input
                    className="input input-sm"
                    placeholder="Ask a follow-up…"
                    value={followUpInput}
                    onChange={(e) => setFollowUpInput(e.target.value)}
                    disabled={followUpAi.busy}
                  />
                  <button type="submit" className="btn btn-primary btn-sm" disabled={followUpAi.busy}>
                    ↑
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
